import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import useErnie from 'shared/hooks/use-ernie';
import useStores from 'shared/hooks/use-stores';
import { getProductWeight, normalizeWeightOption, findChildProductByOption } from 'shared/helpers/products';
import { getLogger } from 'src/utils/datadog-logger';
import { AllotmentStatus, CheckAllotmentDocument } from 'src/gql/graphql';
import type { AllotmentInfo, CartItemInput } from 'src/gql/graphql';

// Mirrors the MobX CartItem class shape (state/cart-item.js) used by mapInputsForPriceCartAPI.
// TODO: Remove once CartItem and CartStore are typed.
type CartItem = {
  product: {
    POSMetaData: {
      canonicalID?: string;
      packageQuantity?: number;
      children?: Array<{ option: string; canonicalID?: string; packageQuantity?: number }>;
    };
  };
  option: string;
  key: string;
  quantity: number;
  basePrice: number;
};

type AllotmentCheckResult = {
  allotmentInfo: AllotmentInfo | null;
  loading: boolean;
  isAllotmentExceeded: boolean;
  allotmentCheckErrorMessage: string | null;
  shouldShowAllotment: boolean;
  shouldBlockOrder: boolean;
};

function mapCartItemsForAllotment(cart: Record<string, CartItem>): CartItemInput[] {
  return Object.values(cart).reduce<CartItemInput[]>((cartItems, item) => {
    const children = item.product.POSMetaData.children ?? [];
    const normalizedItemOption = normalizeWeightOption(item.option);
    const expectedWeight = getProductWeight(normalizedItemOption, 0, 1);
    const childProduct = findChildProductByOption(children, item.option, false);
    const productId = Number(childProduct?.canonicalID ?? item.product.POSMetaData.canonicalID);

    if (!productId) {
      return cartItems;
    }

    // Florida MMUR allotment check pre-validation uses 2-decimal gram precision
    // (the default for getProductWeight, where 2-decimal rounding is documented as intentional for allotment).
    const rawPq = Number(childProduct?.packageQuantity ?? item.product.POSMetaData?.packageQuantity);
    const packageQuantity = Number.isFinite(rawPq) && rawPq > 1 ? rawPq : 1;

    cartItems.push({
      productId,
      ecommKey: item.key,
      quantity: packageQuantity > 1 ? packageQuantity * item.quantity : item.quantity,
      unitPrice: item.basePrice,
      unitWeightGrams: expectedWeight / packageQuantity,
    });

    return cartItems;
  }, []);
}

export function useAllotmentCheck(): AllotmentCheckResult {
  const { Cart } = useStores();
  const showErnie = useErnie();
  const ddLogger = useMemo(() => getLogger('mmur-allotment'), []);

  const { order, dispensary } = Cart;
  const isFloridaMedical = dispensary?.location?.state === 'FL' && order.medicalOrder;
  const floridaAllotmentCheckEnabled = dispensary?.storeSettings?.enableMMURAllotmentCheck ?? false;
  const shouldCheck = isFloridaMedical && floridaAllotmentCheckEnabled;
  const medicalCardNumber = Cart.order.medicalCard?.number;
  const dateOfBirth = Cart.order.medicalCard?.dateOfBirth;

  const cartItems = useMemo(() => {
    if (!shouldCheck || !medicalCardNumber || !dateOfBirth) {
      return [];
    }
    return mapCartItemsForAllotment(order.cart);
  }, [shouldCheck, medicalCardNumber, dateOfBirth, order.cart]);

  const { data, loading, error } = useQuery(CheckAllotmentDocument, {
    variables: {
      input: {
        dispensaryId: dispensary?.id,
        medicalCardNumber,
        dateOfBirth,
        cart: cartItems,
      },
    },
    skip: !shouldCheck || !medicalCardNumber || !dateOfBirth || cartItems.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const allotmentInfo = data?.checkAllotment ?? null;
  const isAllotmentExceeded = allotmentInfo?.status === AllotmentStatus.exceeded;
  const isAllotmentError = allotmentInfo?.status === AllotmentStatus.error;
  const allotmentCheckErrorMessage = allotmentInfo?.error ?? null;
  const isQueryError = Boolean(error);
  const blockOrderOnAllotmentFailure = dispensary?.storeSettings?.blockOrderOnMMURAllotmentFailure ?? false;
  const shouldBlockOrder = blockOrderOnAllotmentFailure && (isAllotmentExceeded || isAllotmentError || isQueryError);

  const allotmentStatus = allotmentInfo?.status ?? null;

  useEffect(() => {
    if (allotmentStatus && shouldCheck) {
      const context = {
        dispensaryId: dispensary?.id,
        status: allotmentStatus,
        blockingEnabled: blockOrderOnAllotmentFailure,
        wouldBlock: shouldBlockOrder,
      };

      if (allotmentStatus === AllotmentStatus.exceeded) {
        ddLogger.warn('allotment check exceeded', context);
      } else if (allotmentStatus === AllotmentStatus.error) {
        ddLogger.warn('allotment check returned error', context);
      }
    }
  }, [allotmentStatus, shouldCheck, dispensary?.id, blockOrderOnAllotmentFailure, shouldBlockOrder, ddLogger]);

  useEffect(() => {
    if (isQueryError) {
      ddLogger.error('allotment check query failed', { dispensaryId: dispensary?.id });
      showErnie('Unable to verify MMUR allotment. Please try again.', 'danger');
    }
  }, [isQueryError, showErnie, ddLogger, dispensary?.id]);

  return {
    allotmentInfo,
    loading: shouldCheck && loading,
    isAllotmentExceeded,
    allotmentCheckErrorMessage,
    shouldShowAllotment: shouldCheck && !isQueryError,
    shouldBlockOrder,
  };
}
